"use client";

import { useEffect, useState } from "react";
import { useAuth, useSignIn } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthCard, authLinkClassName, authPrimaryButtonClassName } from "@/components/auth/AuthCard";
import { PasswordField } from "@/components/auth/PasswordField";
import { VerificationCodeForm } from "@/components/auth/VerificationCodeForm";
import { useVerificationCode } from "@/hooks/useVerificationCode";
import { AppleSignInButton } from "@/components/AppleSignInButton";
import { useNavigate } from "react-router";
import { Loader2, ShieldCheck } from "lucide-react";

export const SignInPage: React.FC = () => {
  const OTP_LENGTH = 6;
  const { isSignedIn } = useAuth();
  const { signIn, fetchStatus } = useSignIn();

  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSecondFactorStep, setIsSecondFactorStep] = useState(false);
  const [secondFactorMethod, setSecondFactorMethod] = useState<"email_code" | "phone_code" | "totp" | "backup_code" | null>(null);

  const {
    verificationCode,
    verificationCodeDigits,
    verificationCodeInputRefs,
    resetVerificationCode,
    focusFirstVerificationCodeInput,
    handleVerificationCodeDigitChange,
    handleVerificationCodeKeyDown,
    handleVerificationCodePaste,
  } = useVerificationCode(OTP_LENGTH);

  const navigate = useNavigate();

  const resolveSupportedSecondFactor = () => {
    const strategies = (signIn?.supportedSecondFactors ?? []).map((factor) => factor.strategy);

    if (strategies.includes("email_code")) return "email_code";
    if (strategies.includes("phone_code")) return "phone_code";
    if (strategies.includes("totp")) return "totp";
    if (strategies.includes("backup_code")) return "backup_code";

    return null;
  };

  const handleSecondFactor = async () => {
    if (!signIn) return;

    const strategy = resolveSupportedSecondFactor();
    setSecondFactorMethod(strategy);

    if (!strategy) {
      setError("This account requires 2FA that isn't supported here.");
      return;
    }

    try {
      if (strategy === "email_code") {
        await signIn.mfa.sendEmailCode();
        setError("A 6-digit code has been sent to your email.");
      } else if (strategy === "phone_code") {
        await signIn.mfa.sendPhoneCode();
        setError("A 6-digit code has been sent to your phone.");
      } else if (strategy === "totp") {
        setError("Enter the code from your authenticator app.");
      } else if (strategy === "backup_code") {
        setError("Enter one of your backup codes.");
      }

      resetVerificationCode();
      setIsSecondFactorStep(true);

      setTimeout(() => focusFirstVerificationCodeInput(), 100);
    } catch (err: any) {
      setError(err?.message || "Failed to send verification code.");
    }
  };

  const handleVerifySecondFactor = async () => {
    if (!signIn || !secondFactorMethod || verificationCode.length !== OTP_LENGTH) {
      setError("Please enter the complete 6-digit code.");
      return;
    }

    try {
      let verifyResult: { error: unknown | null } = { error: null };

      if (secondFactorMethod === "email_code") {
        verifyResult = await signIn.mfa.verifyEmailCode({ code: verificationCode });
      } else if (secondFactorMethod === "phone_code") {
        verifyResult = await signIn.mfa.verifyPhoneCode({ code: verificationCode });
      } else if (secondFactorMethod === "totp") {
        verifyResult = await signIn.mfa.verifyTOTP({ code: verificationCode });
      } else if (secondFactorMethod === "backup_code") {
        verifyResult = await signIn.mfa.verifyBackupCode({ code: verificationCode });
      }

      if (verifyResult.error) throw verifyResult.error;

      if (signIn.status === "complete") {
        await signIn.finalize();
        navigate("/", { replace: true });
      }
    } catch (err: any) {
      console.error("MFA failed:", err);
      setError("Invalid code. Please try again.");
    }
  };

  const handleLogin = async () => {
    setError("");

    if (!signIn || !email.trim() || !password.trim()) return;

    try {
      const { error: signInError } = await signIn.password({
        identifier: email,
        password,
      });

      if (signInError) throw signInError;

      if (signIn.status === "complete") {
        await signIn.finalize();
        navigate("/", { replace: true });
        return;
      }

      if (signIn.status === "needs_client_trust" || signIn.status === "needs_second_factor") {
        await handleSecondFactor();
        return;
      }

      setError(`Additional verification required: ${signIn.status}`);
    } catch (err: any) {
      console.error("Login failed:", err);
      setError("Invalid email or password. Please try again.");
    }
  };

  useEffect(() => {
    if (isSignedIn) {
      navigate("/", { replace: true });
    }
  }, [isSignedIn, navigate]);

  return (
    <AuthCard error={error}>
            {!isSecondFactorStep ? (
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="h-12"
                  />
                </div>

                <PasswordField
                  id="password"
                  label="Password"
                  value={password}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  isVisible={showPassword}
                  onChange={setPassword}
                  onToggleVisibility={() => setShowPassword(!showPassword)}
                  action={
                    <a href="/forgot-password" className="text-sm text-emerald-600 hover:underline">
                      Forgot password?
                    </a>
                  }
                />

                <Button
                  onClick={handleLogin}
                  disabled={fetchStatus === "fetching" || !email.trim() || !password.trim()}
                  className={authPrimaryButtonClassName}
                >
                  {fetchStatus === "fetching" ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>

                <div className="relative flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <span className="relative bg-white px-4 text-sm text-slate-500">or</span>
                </div>

                <AppleSignInButton className="w-full" />

                <div className="text-center">
                  <a
                    href="/signup"
                    className={authLinkClassName}
                  >
                    Don't have an account? <span className="font-medium">Sign up</span>
                  </a>
                </div>
              </div>
            ) : (
              /* 2FA Step */
              <div className="space-y-6">
                <div className="flex flex-col items-center text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <ShieldCheck size={28} />
                  </div>
                  <h3 className="text-xl font-semibold">Two-factor verification</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Enter the 6-digit code sent to your device
                  </p>
                </div>

                <VerificationCodeForm
                  digits={verificationCodeDigits}
                  inputRefs={verificationCodeInputRefs}
                  onDigitChange={handleVerificationCodeDigitChange}
                  onKeyDown={handleVerificationCodeKeyDown}
                  onPaste={handleVerificationCodePaste}
                  onVerify={handleVerifySecondFactor}
                />

                <Button
                  variant="outline"
                  onClick={() => {
                    setIsSecondFactorStep(false);
                    setError("");
                  }}
                  className="w-full"
                >
                  Back to sign in
                </Button>
              </div>
            )}
    </AuthCard>
  );
};